// A WebSocket that redials, with the two behaviours this app actually needs and
// the library we used to depend on could not give us:
//
//  1. A FRESH auth token on every attempt. The token rides in the WebSocket
//     subprotocol, and `reconnecting-websocket` captured the protocols array once
//     at construction — so after the access token expired, every redial presented
//     the stale one and could never succeed again.
//  2. No message queue. Input is a full-state snapshot sent every frame, so a
//     message that could not be sent is worthless by the time the socket is back:
//     replaying the backlog just injects stale clicks and key states into the app.
//
// Everything else that library offered (buffering, ping/pong, per-message
// timeouts, debug modes) was unused.

const DEFAULT_MIN_DELAY_MS = 300;
const DEFAULT_MAX_DELAY_MS = 4000;

export class ReconnectingSocket {
	#url;
	#protocols;
	#minDelay;
	#maxDelay;
	#listeners = {message: [], open: [], close: [], error: []};
	#socket = null;
	#retryTimer = null;
	#attempt = 0;
	#closed = false;

	// protocols is a FUNCTION so the value is re-read on every attempt.
	constructor(
		url,
		protocols,
		{minDelay = DEFAULT_MIN_DELAY_MS, maxDelay = DEFAULT_MAX_DELAY_MS} = {},
	) {
		this.#url = url;
		this.#protocols = protocols;
		this.#minDelay = minDelay;
		this.#maxDelay = maxDelay;
		this.#connect();
	}

	get readyState() {
		return this.#socket ? this.#socket.readyState : WebSocket.CONNECTING;
	}

	get bufferedAmount() {
		return this.#socket ? this.#socket.bufferedAmount : 0;
	}

	addEventListener(type, fn) {
		if (this.#listeners[type]) this.#listeners[type].push(fn);
	}

	removeEventListener(type, fn) {
		const list = this.#listeners[type];
		if (!list) return;
		const i = list.indexOf(fn);
		if (i >= 0) list.splice(i, 1);
	}

	// Dropped rather than queued when the socket is not open: see (2) above.
	send(data) {
		if (this.#socket && this.#socket.readyState === WebSocket.OPEN) {
			this.#socket.send(data);
			return true;
		}
		return false;
	}

	close() {
		this.#closed = true;
		if (this.#retryTimer) {
			clearTimeout(this.#retryTimer);
			this.#retryTimer = null;
		}
		if (this.#socket) {
			// Drop our handlers first so a close we asked for is not reported as a
			// disconnect the UI should react to.
			this.#socket.onopen =
				this.#socket.onmessage =
				this.#socket.onclose =
				this.#socket.onerror =
					null;
			try {
				this.#socket.close();
			} catch {}
			this.#socket = null;
		}
	}

	#emit(type, event) {
		for (const fn of this.#listeners[type].slice()) {
			try {
				fn(event);
			} catch (e) {
				console.error(`error in ${type} listener`, e);
			}
		}
	}

	#connect() {
		if (this.#closed) return;

		let socket;
		try {
			socket = new WebSocket(this.#url, this.#protocols());
		} catch (e) {
			// A synchronous throw (bad URL, bad subprotocol) still deserves a retry:
			// the token may simply have been missing this instant.
			this.#emit("error", e);
			this.#scheduleRetry();
			return;
		}

		// Frames are parsed synchronously from an ArrayBuffer; a Blob would add an
		// await per frame. Nothing in this app wants the Blob behaviour.
		socket.binaryType = "arraybuffer";
		this.#socket = socket;

		socket.onopen = (event) => {
			this.#attempt = 0;
			this.#emit("open", event);
		};
		socket.onmessage = (event) => this.#emit("message", event);
		socket.onerror = (event) => this.#emit("error", event);
		socket.onclose = (event) => {
			if (this.#closed) return;
			this.#socket = null;
			this.#emit("close", event);
			this.#scheduleRetry();
		};
	}

	#scheduleRetry() {
		if (this.#closed || this.#retryTimer) return;
		// Exponential backoff with a cap, so a server restart is picked up quickly
		// but a sustained outage does not hammer it.
		const delay = Math.min(this.#maxDelay, this.#minDelay * Math.pow(2, this.#attempt));
		this.#attempt++;
		this.#retryTimer = setTimeout(() => {
			this.#retryTimer = null;
			this.#connect();
		}, delay);
	}
}
